import React from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { IconChevronRight, SkeletonLabel } from '@noths/polaris-client-ribbons-design-system';
import dynamic from 'next/dynamic';

import * as styles from 'src/components/organisms/ProductReviews/components/ProductReviews/ProductReviews.styles';
import { ERROR_REVIEWS, NO_REVIEWS } from 'src/components/organisms/ProductReviews/constants/copy';
import { getReviewsDrawerURLFragment } from 'src/fragmentIDs';
import type { ProductReviewsState } from 'src/redux/reviews/slice';
import { FeaturedReview } from '../ProductReview/FeaturedReview';

const ProductReviewsDrawer = dynamic(() =>
  import(
    /* webpackChunkName: "delivery_returns_drawer" */ '../ProductReviewDrawer/ProductReviewsDrawer'
  ).then((mod) => mod.ProductReviewsDrawer),
);

export interface ProductReviewsProps {
  onVisible: () => void;
  productCode: number;
  reviews: ProductReviewsState;
  /**
   * From the Feefo breakdown or list, the same source as the product summary, so the two
   * cannot show different totals. The rar-data-api pagination below only drives the pages
   * until Increment C retires that service.
   */
  totalCount: number;
}

export const ProductReviews = ({
  onVisible,
  productCode,
  reviews,
  totalCount,
}: ProductReviewsProps) => {
  const title = totalCount === 1 ? '1 Product review' : `${totalCount} Product reviews`;

  const { listError, pages } = reviews;
  const productReviewsRef = useImpressionRef<HTMLDivElement>(() => onVisible());
  const buttonText = totalCount === 1 ? 'See 1 review' : `See all ${totalCount} reviews`;
  const reviewsPlaceholderItems = Array.from({ length: 3 });

  if (pages === null) {
    return (
      <div css={styles.placeholderContainer}>
        {reviewsPlaceholderItems.map((_, index) => (
          <SkeletonLabel height={52} key={`skeleton-${index}`} labelHeight="XL" />
        ))}
      </div>
    );
  }

  if (listError && Object.keys(pages).length === 0) {
    return <p css={styles.noReviews}>{ERROR_REVIEWS}</p>;
  }

  if (pages && pages[1]?.length === 0) {
    return <p css={styles.noReviews}>{NO_REVIEWS}</p>;
  }

  return (
    <div css={styles.reviewsContainer} ref={productReviewsRef}>
      <div css={styles.scrollable}>
        <ul
          css={styles.reviewsList}
          style={
            {
              '--listWidth': pages[1].length === 1 ? '100%' : '760px',
            } as styles.ReviewItemCSS
          }
        >
          {pages[1].map((review) => (
            <FeaturedReview
              href={`#${getReviewsDrawerURLFragment(review.id)}`}
              key={`review-${review.id}`}
              review={review}
            />
          ))}
        </ul>
      </div>
      <a css={styles.drawerLink} href={`#${getReviewsDrawerURLFragment()}`}>
        {buttonText}
        <IconChevronRight color="var(--brand-purple-base)" height={16} width={16} />
      </a>
      <ProductReviewsDrawer productCode={productCode} title={title} />
    </div>
  );
};
